import axios from 'axios';
import { RECORDING_ENDPOINT, RECORDING_SERVER_API_KEY } from '@/Utils/vice-constants';

class RecordingApiService {
  constructor() {
    this.baseURL = RECORDING_ENDPOINT;
    this.apiToken = RECORDING_SERVER_API_KEY;
    this.apiClient = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getApiToken()}`,
      },
    });
  }

  getApiToken() {
    // ToDo: Implement secure way to retrieve the API token
    return this.apiToken;
  }

  async startRecordingSession(sessionId, clinicianId, enabledDevices = []) {
    try {
      const response = await this.apiClient.post(route('recording.session.start'), {
        session_id: sessionId,
        clinician_id: clinicianId,
        enabled_devices: enabledDevices,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to start recording session:', error);
      throw error;
    }
  }

  async endRecordingSession(sessionId) {
    try {
      const response = await this.apiClient.post(route('recording.session.end'), {
        session_id: sessionId,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to end recording session:', error);
      throw error;
    }
  }

  // Notify the server that a recording has started
  async notifyRecordingStarted(
    recordingId,
    sessionId,
    universityId,
    streamId,
    deviceType,
    metadata = {},
  ) {
    try {
      const response = await this.apiClient.post(route('recording.start'), {
        recording_id: recordingId,
        session_id: sessionId,
        university_id: universityId,
        stream_id: streamId,
        device_type: deviceType,
        metadata,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to notify recording started:', error);
      throw error;
    }
  }

  // Notify the server that a recording has stopped
  async notifyRecordingStopped(recordingId, sessionId, universityId) {
    try {
      const response = await this.apiClient.post(route('recording.stop'), {
        recording_id: recordingId,
        session_id: sessionId,
        university_id: universityId,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to notify recording stopped:', error);
      throw error;
    }
  }

  async notifyChunkReceived(recordingId, chunkIndex, chunkSize, timestamp) {
    try {
      const response = await this.apiClient.post(route('recording.chunk'), {
        recording_id: recordingId,
        chunk_index: chunkIndex,
        chunk_size: chunkSize,
        timestamp,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to notify chunk received:', error);
      // Don't throw for chunk notifications - they're not critical
      return null;
    }
  }

  async getSessionRecordings(sessionId) {
    try {
      const response = await this.apiClient.get(
        route('recording.session.get', { session: sessionId }),
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get session recordings:', error);
      throw error;
    }
  }

  async getRecording(recordingId) {
    try {
      const response = await this.apiClient.get(route('recording.get', { recordingId }));
      return response.data;
    } catch (error) {
      console.error('Failed to get recording:', error);
      throw error;
    }
  }

  async deleteRecording(recordingId) {
    try {
      const response = await this.apiClient.delete(`/recordings/${recordingId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete recording:', error);
      throw error;
    }
  }

  async getRecordingServerStatus() {
    try {
      const response = await this.apiClient.get(route('recording.server.status'));
      return response.data;
    } catch (error) {
      console.error('Failed to get recording server status:', error);
      throw error;
    }
  }
}

export default RecordingApiService;
